import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type { Role, RoleFilters } from "../types/roles-and-permissions.types";

const ADMIN_ENDPOINT = "/api/admin/roles";

/** API response: { success, status, data: RoleApiItem[] | RoleApiItem, message?, count? } */
interface RoleListResponse {
  success?: boolean;
  status?: number;
  data?: RoleApiItem[] | RoleApiItem;
  message?: string;
  count?: number;
  total?: number;
}

/** API'den gelen rol item - backend: id, domainID, name, type, isDefault, createdAt, updatedAt, permissionKeys */
interface RoleApiItem {
  id: string;
  domainID?: string;
  name?: string;
  roleName?: string;
  description?: string;
  roleDescription?: string;
  type?: number;
  isDefault?: boolean;
  assignedPersonCount?: number;
  assignedUserCount?: number;
  usersCount?: number;
  createdAt?: string;
  updatedAt?: string;
  permissionKeys?: string;
  [key: string]: unknown;
}

function mapApiRoleToRole(item: RoleApiItem): Role {
  const name = item.name ?? item.roleName ?? "";
  const description = item.description ?? item.roleDescription ?? "";
  const count =
    item.assignedPersonCount ??
    item.assignedUserCount ??
    item.usersCount ??
    0;
  const formatDate = (d: string | undefined) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return d;
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };
  return {
    id: item.id,
    roleName: name,
    roleDescription: description,
    type: item.type,
    isDefault: item.isDefault,
    permissionKeys:
      typeof item.permissionKeys === "string" ? item.permissionKeys : undefined,
    assignedPersonCount: typeof count === "number" ? count : 0,
    createdAt: formatDate(item.createdAt),
    updatedAt: formatDate(item.updatedAt),
  };
}

export interface CreateRolePayload {
  name: string;
  description?: string;
  permissionKeys: string;
  type?: number;
  isDefault?: boolean;
}

export interface UpdateRolePayload {
  name?: string;
  description?: string;
  permissionKeys?: string;
  type?: number;
  isDefault?: boolean;
}

export const rolesAndPermissionsApi = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: RoleFilters;
  }): Promise<{ data: Role[]; total: number }> => {
    const query: Record<string, unknown> = {
      page: params?.page ?? 1,
      limit: params?.pageSize ?? 10,
    };
    if (params?.filters?.search) query.search = params.filters.search;
    if (typeof params?.filters?.roleType === "number") {
      query.roleType = params.filters.roleType;
    }
    if (params?.filters?.sortBy) query.sortBy = params.filters.sortBy;
    if (params?.filters?.sortOrder) query.sortOrder = params.filters.sortOrder;

    const res = await apiClient.get<RoleListResponse>(ADMIN_ENDPOINT, {
      params: query,
    });
    const data = res.data;

    // Backend: { success, status, data: RoleApiItem[], message, count }
    const rawList = data?.data;
    const list: RoleApiItem[] = Array.isArray(rawList)
      ? rawList
      : Array.isArray((data as { items?: RoleApiItem[] })?.items)
        ? (data as { items: RoleApiItem[] }).items
        : [];
    const total = data?.count ?? (data as { total?: number })?.total ?? list.length;

    return {
      data: list.map(mapApiRoleToRole),
      total,
    };
  },

  getById: async (
    id: string,
  ): Promise<Omit<Role, "permissionKeys"> & { permissionKeys?: string[]; type?: number }> => {
    const res = await apiClient.get<RoleListResponse>(
      `${ADMIN_ENDPOINT}/${id}`,
    );
    const data = res.data;
    const item = (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!item?.id) throw new Error(i18n.t("rolesApi.notFound"));

    const role = mapApiRoleToRole(item);
    const type = item.type;
    const keys = item.permissionKeys;
    const permissionKeys: string[] =
      typeof keys === "string"
        ? keys.split(/,\s*/).map((p) => p.trim()).filter(Boolean)
        : Array.isArray(keys)
          ? (keys as unknown[]).map((k) => String(k))
          : [];

    return { ...role, permissionKeys, type };
  },

  create: async (payload: CreateRolePayload): Promise<Role> => {
    const res = await apiClient.post<RoleListResponse>(ADMIN_ENDPOINT, payload);
    const data = res.data;
    const role = (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!role?.id) throw new Error(i18n.t("rolesApi.createFailed"));
    return mapApiRoleToRole(role);
  },

  update: async (id: string, payload: UpdateRolePayload): Promise<Role> => {
    const res = await apiClient.patch<RoleListResponse>(
      `${ADMIN_ENDPOINT}/${id}`,
      payload,
    );
    const data = res.data;
    const role = (data?.data as RoleApiItem) ?? (data as unknown as RoleApiItem);
    if (!role?.id) throw new Error(i18n.t("rolesApi.updateFailed"));
    return mapApiRoleToRole(role);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_ENDPOINT}/${id}`);
  },
};
